<script setup lang="ts">
import {computed} from 'vue'
import {useNewUiEnabled} from '@/composable/useNewUiEnabled'
import HotelFilter from './HotelFilter.vue'
import LogoutProfileMenu from './LogoutProfileMenu.vue'
import MenuItems from './MenuItems.vue'
import CompanyButton from './CompanyButton.vue'

type Emit = {
  toggleMenu: []
}

interface Props {
  sidebar: boolean
  showMenu: boolean
  showMenuItems: boolean
}

defineEmits<Emit>()
const props = defineProps<Props>()

const isNewUiEnabled = useNewUiEnabled()

const dividerClasses = computed(() => (isNewUiEnabled.value ? 'border-new-neutral-300' : 'border-gray-300'))

const classes = computed(() => ({
  'w-96': props.sidebar,
  'py-new-3xl': props.showMenu && isNewUiEnabled.value,
  '!pt-new-lg': props.sidebar && props.showMenu && isNewUiEnabled.value,
  'w-full px-5': !props.sidebar && !isNewUiEnabled.value,
  'w-full px-new-3xl': !props.sidebar && isNewUiEnabled.value,
}))

const menuClasses = computed(() => ({
  'group text-left': true,
  '-translate-y-[20px] opacity-0 duration-500': props.showMenu === false,
  'translate-y-0 opacity-100 duration-500': props.showMenu === true,
}))

const menuItemClasses = computed(() => ({
  'text-base': true,
  'text-new-tertiary-500 hover:text-new-tertiary-100 group-hover:text-new-tertiary-100': isNewUiEnabled.value,
  'text-brandlight hover:text-white group-hover:text-white': !isNewUiEnabled.value,
  'tracking-widest uppercase cursor-pointer antialiased': true,
}))

const subMenuClasses = computed(() => ({
  'flex flex-col text-base tracking-widest uppercase': true,
  'px-new-md py-new-lg gap-y-[10px] text-new-tertiary-500 hover:text-new-tertiary-100': isNewUiEnabled.value,
  'pl-5 pt-2 gap-y-2 text-brandlight hover:text-white': !isNewUiEnabled.value,
}))

const subMenuItemClasses = computed(() => [
  isNewUiEnabled.value ? 'text-new-tertiary-500 hover:text-new-tertiary-100' : 'text-brandlight hover:text-white',
  'tracking-normal uppercase cursor-pointer antialiased',
])
</script>

<template>
  <ul class="flex flex-col bg-inherit z-header" :class="[isNewUiEnabled ? 'gap-y-new-3xl' : 'gap-y-5', classes]">
    <template v-if="showMenuItems">
      <MenuItems
        :is-burger-menu="true"
        :menu-classes="menuClasses"
        :menu-item-classes="menuItemClasses"
        :sub-menu-classes="subMenuClasses"
        :sub-menu-item-classes="subMenuItemClasses"
        @toggle-menu="$emit('toggleMenu')"
      ></MenuItems>

      <li><hr class="border-t w-full" :class="dividerClasses" /></li>
    </template>

    <li :class="menuClasses">
      <LogoutProfileMenu :class="menuItemClasses" :light="false" @toggle-menu="$emit('toggleMenu')"></LogoutProfileMenu>
    </li>

    <template v-if="!sidebar">
      <li><hr class="border-t w-full" :class="dividerClasses" /></li>
      <CompanyButton class="w-full" variant="invert"></CompanyButton>
    </template>

    <li><hr class="border-t w-full" :class="dividerClasses" /></li>

    <li :class="menuClasses">
      <HotelFilter :class="subMenuItemClasses" :light="false" :scrollable="false"></HotelFilter>
    </li>
  </ul>
</template>
